import * as React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Badge } from './badge';
import { useTenancy } from '../../contexts/TenancyContext';
import { toast } from 'sonner';
import { getAllObjectives, Objective } from '@/services/objectivesService';

// Re-export the Objective type from the service
export type { Objective } from '@/services/objectivesService';

interface ObjectivesComboboxProps {
  selected: string[];
  setSelected: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  onObjectiveSelect?: (objectiveId: string) => void;
  onObjectiveDeselect?: (objectiveId: string) => void;
  onAllSelect?: () => void;
  onAllDeselect?: () => void;
  objectives?: Objective[];
  fetchObjectives?: () => Promise<Objective[]>;
}

// Context for objectives data
interface ObjectivesContextType {
  objectives: Objective[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<Objective[]>;
}

const ObjectivesContext = React.createContext<ObjectivesContextType | undefined>(undefined);

// Provider component
export const ObjectivesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [objectives, setObjectives] = React.useState<Objective[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const { activeTenancy } = useTenancy();
  
  const fetchObjectives = React.useCallback(async () => {
    console.log('[ObjectivesCombobox] Fetching objectives...');
    if (!activeTenancy?.id) {
      console.warn('[ObjectivesCombobox] No active tenancy ID');
      setLoading(false);
      return [];
    }

    setLoading(true);
    try {
      console.log(`[ObjectivesCombobox] Calling getAllObjectives with companyID:`, activeTenancy.id);
      const data = await getAllObjectives(activeTenancy.id);
      console.log('[ObjectivesCombobox] Received objectives:', data);
      setObjectives(data);
      setError(null);
      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch objectives');
      console.error('Error fetching objectives:', error);
      setError(error);
      toast.error('Failed to load objectives');
      return [];
    } finally {
      setLoading(false);
    }
  }, [activeTenancy?.id]);

  // Initial fetch
  React.useEffect(() => {
    console.log('[ObjectivesCombobox] useEffect triggered, activeTenancy:', activeTenancy);
    fetchObjectives().catch(error => {
      console.error('[ObjectivesCombobox] Error in fetchObjectives:', error);
      setError(error instanceof Error ? error : new Error(String(error)));
    });
  }, [fetchObjectives, activeTenancy]);

  return (
    <ObjectivesContext.Provider value={{ objectives, loading, error, refresh: fetchObjectives }}>
      {children}
    </ObjectivesContext.Provider>
  );
};

// Hook to use the objectives context
export const useObjectives = () => {
  const context = React.useContext(ObjectivesContext);
  if (!context) {
    throw new Error('useObjectives must be used within an ObjectivesProvider');
  }
  return context;
};

// Main component
export const ObjectivesCombobox: React.FC<ObjectivesComboboxProps> = ({
  selected,
  setSelected,
  placeholder = "Select objectives...",
  className,
  fullWidth = false,
  onObjectiveSelect,
  onObjectiveDeselect,
  onAllSelect,
  onAllDeselect,
  objectives: propObjectives,
  fetchObjectives: propFetchObjectives,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const { objectives: contextObjectives, loading, error, refresh } = useObjectives();
  
  // Use prop objectives if provided, otherwise use context
  const objectives = propObjectives || contextObjectives || [];
  
  // Filter objectives based on search query
  const filteredObjectives = React.useMemo(() => {
    if (!searchQuery) return objectives;
    const query = searchQuery.toLowerCase();
    return objectives.filter(
      objective => 
        objective.objectiveText.toLowerCase().includes(query) ||
        objective.id.toLowerCase().includes(query) /*||
        objective.owner.toLowerCase().includes(query)*/
    );
  }, [objectives, searchQuery]);

  // Toggle objective selection
  const toggleObjective = (objectiveId: string) => {
    const newSelected = selected.includes(objectiveId)
      ? selected.filter(id => id !== objectiveId)
      : [...selected, objectiveId];
      
    setSelected(newSelected);
    
    // Call appropriate callback
    if (selected.includes(objectiveId)) {
      onObjectiveDeselect?.(objectiveId);
    } else {
      onObjectiveSelect?.(objectiveId);
    }
  };

  // Toggle all objectives
  const toggleSelectAll = () => {
    if (selected.length === objectives.length) {
      setSelected([]);
      onAllDeselect?.();
    } else {
      setSelected(objectives.map(obj => obj.id));
      onAllSelect?.();
    }
  };

  // Get objective by ID
  const getObjectiveById = (id: string) => 
    objectives.find(obj => obj.id === id);

  // Format date for display
  const formatDate = (date: any) => {
    if (!date) return 'No date';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleDateString();
    } catch (e) {
      return 'Invalid date';
    }
  };

  return (
    <div className={cn("relative", fullWidth ? 'w-full' : 'w-[300px]', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between overflow-hidden hover:bg-primary/2",
              selected.length > 0 ? "h-auto py-1" : "h-10"
            )}
          >
            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
              {selected.length > 0 ? (
                selected.map(id => {
                  const objective = getObjectiveById(id);
                  if (!objective) return null;
                  
                  return (
                    <div
                      key={id}
                      className="flex items-center gap-1 bg-primary/10 text-primary rounded-md px-2 py-1 text-sm"
                    >
                      <span className="max-w-[200px] truncate">
                        {objective.objectiveText || `Objective ${objective.id}`}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleObjective(id);
                        }}
                        className="ml-1 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false} className="flex flex-col h-full">
            <div className="border-b px-3 pt-3">
              <CommandInput
                placeholder="Search objectives..."
                value={searchQuery}
                onValueChange={setSearchQuery}
                className="border-0 focus:ring-0 focus-visible:ring-0"
              />
              
              {objectives.length > 0 && (
                <div className="flex justify-between items-center py-2 text-xs text-muted-foreground">
                  <span>
                    {selected.length} of {objectives.length} selected
                  </span>
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="text-primary hover:underline text-xs"
                  >
                    {selected.length === objectives.length ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
              )}
            </div>
            
            <CommandList 
              className="max-h-[400px] overflow-auto"
              onWheel={(e) => {
                // Prevent wheel events from propagating to parent elements
                e.stopPropagation();
              }}
            >
              {loading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading objectives...
                </div>
              ) : error ? (
                <div className="py-6 text-center text-sm text-destructive">
                  Failed to load objectives. <button onClick={refresh} className="text-primary hover:underline">Retry</button>
                </div>
              ) : filteredObjectives.length === 0 ? (
                <CommandEmpty className="py-6">No objectives found.</CommandEmpty>
              ) : (
                <CommandGroup>
                  {filteredObjectives.map((objective) => {
                    const isSelected = selected.includes(objective.id);
                    return (
                      <CommandItem
                        key={objective.id}
                        value={objective.id}
                        onSelect={() => toggleObjective(objective.id)}
                        className="flex flex-col items-start gap-1 p-3"
                      >
                        <div className="flex items-center w-full">
                          <div
                            className={cn(
                              "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                              isSelected
                                ? "bg-primary text-primary-foreground"
                                : "opacity-50 [&_svg]:invisible"
                            )}
                          >
                            <Check className={cn("h-4 w-4")} />
                          </div>
                          <span className="font-medium">
                            {objective.objectiveText || `Objective ${objective.id}`}
                          </span>
                        </div>
                        <div className="ml-6 mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <span>ID: {objective.id}</span>
                          {/* <span>•</span>
                          <span>Owner: {objective.owner}</span> */}
                          <span>•</span>
                          <span>Target: {formatDate(objective.objectiveTargetDate)}</span>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ObjectivesCombobox;
